import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenuToggle,
  NavbarMenu,
  NavbarMenuItem,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Avatar,
  Image,
} from '@heroui/react';
import { BookOpen, LogOut, Package, Search, User, Users, GraduationCap } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from '../LanguageSwitcher';
import ThemeSwitcher from '../ThemeSwitcher';
import { useCommonTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const Nav = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useCommonTranslation();
  const location = useLocation();
  const { user, logout } = useAuth();

  // Check if user has admin or creator role
  const hasAdminAccess = user?.role === 'admin' || user?.role === 'creator';

  const baseNavigation = [
    { name: t('nav.products'), href: '/products', icon: Package },
    { name: t('nav.guides'), href: '/guides', icon: BookOpen },
    { name: t('nav.courses'), href: '/courses', icon: GraduationCap },
    { name: t('nav.people'), href: '/people', icon: Users },
  ];

  const adminNavigation = hasAdminAccess ? [{ name: t('nav.admin'), href: '/admin', icon: User }] : [];

  const navigation = [...baseNavigation, ...adminNavigation];

  const checkIsActive = (href: string) => {
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <Navbar onMenuOpenChange={setIsMenuOpen} isMenuOpen={isMenuOpen} className="sticky top-0 z-40 h-20" maxWidth="full">
      <NavbarContent justify="center">
        <NavbarMenuToggle aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} className="lg:hidden" />
        <NavbarBrand className="mr-3">
          <Link to="/" className="flex items-center space-x-3">
            <Image
              src="https://viaphoton.com/wp-content/uploads/2021/08/vP_Logo-FullColor-Hz.svg"
              alt="viaPhoton Logo"
              className="max-w-none"
              width={200}
            />
          </Link>
        </NavbarBrand>
        {navigation.map((item) => {
          const Icon = item.icon;
          return (
            <NavbarItem key={item.name} isActive className="hidden lg:flex">
              <Link
                to={item.href}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  checkIsActive(item.href)
                    ? 'bg-primary/20 text-primary font-semibold'
                    : 'text-foreground/70 hover:bg-default-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-semibold text-base">{item.name}</span>
              </Link>
            </NavbarItem>
          );
        })}
      </NavbarContent>

      <NavbarContent justify="end">
        {/* Search bar - Desktop */}
        {/* <NavbarItem className="hidden lg:flex">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder={`${t('common.search')} products, tutorials, and documentation...`}
              className="w-80"
              size="lg"
              startContent={<Search className="w-5 h-5 text-gray-400" />}
              endContent={
                <div className="flex items-center space-x-1 text-sm text-gray-400">
                  <Command className="w-4 h-4" />
                  <kbd className="px-2 py-1 bg-gray-200 rounded text-sm">K</kbd>
                </div>
              }
            />
          </div>
        </NavbarItem> */}

        {/* Mobile search button */}
        <NavbarItem className="hidden lg:hidden">
          <Button
            isIconOnly
            variant="ghost"
            size="lg"
            onPress={() => {
              /* TODO: Implement mobile search */
            }}
            aria-label="Search"
          >
            <Search className="w-6 h-6" />
          </Button>
        </NavbarItem>

        {/* Language Switcher */}
        <NavbarItem className="hidden lg:flex">
          <LanguageSwitcher />
        </NavbarItem>

        {/* Theme Switcher */}
        <NavbarItem className="hidden lg:flex">
          <ThemeSwitcher />
        </NavbarItem>

        {/* Debug indicators */}

        {/* User menu */}
        <NavbarItem>
          <Dropdown>
            <DropdownTrigger>
              <Avatar
                isBordered
                as="button"
                className="transition-transform cursor-pointer"
                color="primary"
                name={`${user?.firstName} ${user?.lastName}` || 'User'}
                size="md"
                src={user?.avatarFile?.url || ''}
              />
            </DropdownTrigger>
            <DropdownMenu aria-label="User menu actions" variant="flat">
              <DropdownItem
                key="logout"
                color="danger"
                startContent={<LogOut className="w-4 h-4" />}
                onPress={handleLogout}
              >
                Logout
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </NavbarItem>
      </NavbarContent>

      <NavbarMenu>
        {navigation.map((item, index) => {
          const Icon = item.icon;
          return (
            <NavbarMenuItem key={`${item.name}-${index}`}>
              <Link
                to={item.href}
                className={`w-full flex items-center space-x-3 px-4 py-4 rounded-lg transition-colors ${
                  checkIsActive(item.href)
                    ? 'bg-primary/20 text-primary font-semibold'
                    : 'text-foreground/70 hover:bg-default-100'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                <Icon className="w-6 h-6" />
                <span className="font-semibold text-lg">{item.name}</span>
              </Link>
            </NavbarMenuItem>
          );
        })}
      </NavbarMenu>
    </Navbar>
  );
};

export default Nav;
